import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
  RefObject,
  useCallback,
} from "react";

interface ScrollContextProps {
  isScrolled: boolean;
  setScrollRef: (ref: RefObject<HTMLDivElement>) => void;
  scrollToTop: () => void;
}

const ScrollContext = createContext<ScrollContextProps | undefined>(undefined);

export const ScrollProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollRef, setScrollRef] = useState<RefObject<HTMLDivElement> | null>(
    null,
  );

  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef && scrollRef.current) {
        const isScrolled = scrollRef.current.scrollTop > 0;
        setIsScrolled(isScrolled);
      }
    };

    const currentRef = scrollRef?.current;
    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
      return () => {
        currentRef.removeEventListener("scroll", handleScroll);
      };
    }
  }, [scrollRef]);

  const scrollToTop = useCallback(() => {
    if (scrollRef && scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [scrollRef]);

  return (
    <ScrollContext.Provider value={{ isScrolled, setScrollRef, scrollToTop }}>
      {children}
    </ScrollContext.Provider>
  );
};

export const useScroll = () => {
  const context = useContext(ScrollContext);
  if (!context) {
    throw new Error("useScroll must be used within a ScrollProvider");
  }
  return context;
};
